import type { PasskeyAuthenticationOptions, PasskeyRegistrationOptions } from '@outpost/api-client';

/**
 * The browser half of a passkey ceremony.
 *
 * The API speaks the WebAuthn JSON form, where every binary field is base64url;
 * `navigator.credentials` speaks ArrayBuffers. This is that conversion, in one
 * place, so no page has to think about it.
 *
 * Written by hand rather than taken from a library: it is a few dozen lines of
 * encoding with no decisions in it, and the dashboard's dependencies all ship
 * to the browser.
 */

/** Whether this browser can do passkeys at all. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

/**
 * Returns an `ArrayBuffer` rather than a view: that is what the credential
 * options accept, and a view over a possibly-shared buffer is not.
 */
function decode(value: string): ArrayBuffer {
  // The API sends unpadded base64url; atob wants padded base64.
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));

  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function encode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Turn whatever the browser throws into something worth showing.
 *
 * A cancelled ceremony arrives as the same `NotAllowedError` as a timeout, and
 * its own message is a paragraph of specification prose.
 */
function describe(error: unknown): Error {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError') {
      return new Error('No passkey was used. Try again when you are ready.');
    }
    if (error.name === 'InvalidStateError') {
      return new Error('This device already has a passkey for your account.');
    }
    if (error.name === 'SecurityError') {
      return new Error(
        'This page is not served from the address Outpost is configured with, so passkeys cannot be used here.',
      );
    }
  }
  return error instanceof Error ? error : new Error('Something went wrong with the passkey.');
}

/** Create a passkey, returning the credential in the form the API expects. */
export async function createPasskey(options: PasskeyRegistrationOptions) {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        ...options,
        challenge: decode(options.challenge),
        user: { ...options.user, id: decode(options.user.id) },
        pubKeyCredParams: options.pubKeyCredParams.map((parameter) => ({
          ...parameter,
          type: parameter.type as PublicKeyCredentialType,
        })),
        attestation: options.attestation as AttestationConveyancePreference,
        authenticatorSelection: {
          residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement,
          requireResidentKey: options.authenticatorSelection.requireResidentKey,
          userVerification: options.authenticatorSelection
            .userVerification as UserVerificationRequirement,
        },
        excludeCredentials: options.excludeCredentials.map((descriptor) => ({
          type: descriptor.type as PublicKeyCredentialType,
          id: decode(descriptor.id),
        })),
      },
    });
  } catch (error) {
    throw describe(error);
  }

  const response = (credential as PublicKeyCredential | null)?.response as
    AuthenticatorAttestationResponse | undefined;
  if (!credential || !response) throw new Error('The browser returned no passkey.');

  return {
    response: {
      clientDataJSON: encode(response.clientDataJSON),
      attestationObject: encode(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
  };
}

/** Sign in with a passkey, returning the assertion in the form the API expects. */
export async function requestPasskey(options: PasskeyAuthenticationOptions) {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.get({
      publicKey: {
        challenge: decode(options.challenge),
        rpId: options.rpId,
        timeout: options.timeout,
        userVerification: options.userVerification as UserVerificationRequirement,
        // Deliberately empty: the credential is discoverable, so the
        // authenticator offers the accounts it holds and nobody has to type a
        // username first.
        allowCredentials: [],
      },
    });
  } catch (error) {
    throw describe(error);
  }

  const assertion = credential as PublicKeyCredential | null;
  const response = assertion?.response as AuthenticatorAssertionResponse | undefined;
  if (!assertion || !response) throw new Error('The browser returned no passkey.');

  return {
    id: assertion.id,
    response: {
      clientDataJSON: encode(response.clientDataJSON),
      authenticatorData: encode(response.authenticatorData),
      signature: encode(response.signature),
      userHandle: response.userHandle ? encode(response.userHandle) : null,
    },
  };
}
